// The Hub's light/dark mode, for anything rendered on top of it. It lives in the member's hub metadata, which the
// color-mode switch updates optimistically (see the background-image route's clientAction), so the new mode is here
// on the click and the fade starts with it. HQ reads the same setting with its own default (see ~/lib/hq-color-mode).
import { hashKey, type DehydratedState } from '@tanstack/react-query';
import { useLocation, useMatches } from 'react-router';

import { hqModeForPath, resolveHqColorMode, resolveSharedColorMode } from '~/lib/hq-color-mode';
import { hubMetadataQuery, type HubMetadata } from '~/lib/hub-metadata/hub-metadata-query';
import { useOptionalHubMetadata } from '~/lib/hub-metadata/use-hub-metadata';
import { resolveColorMode, type ColorMode } from '~/routes/_app+/_layout/helpers/color-mode';
import { useIsomorphicLayoutEffect } from '~/utils/dom';

function colorModeFor(pathname: string, background: string | undefined): ColorMode {
  return resolveSharedColorMode({
    shellMode: resolveColorMode(background),
    hqMode: hqModeForPath(pathname, resolveHqColorMode(background)),
  });
}

export function useColorMode(): ColorMode {
  const hubMetadata = useOptionalHubMetadata();
  const { pathname } = useLocation();
  // Falls back to light (dark text) outside the Hub layout, where no member's settings are loaded.
  return hubMetadata ? colorModeFor(pathname, hubMetadata.background) : 'light';
}

export function useIsDarkMode(): boolean {
  return useColorMode() === 'dark';
}

/** Only HQ opts the document and shared shell into the NASA palette. */
export function useHqColorMode(): ColorMode | undefined {
  const { pathname } = useLocation();
  return hqModeForPath(pathname, useColorMode());
}

/**
 * The HQ mode root's <html data-hq-theme> renders with. <html> sits above the query cache, so it reads the member's
 * setting from the layout loader's dehydrated cache: right for the server render and the hydrating one, which must
 * match it. The layout never reloads that data, so after a switch useSyncHqTheme keeps the attribute current.
 */
export function useInitialHqColorMode(): ColorMode | undefined {
  const { pathname } = useLocation();
  const hubMetadataKey = hashKey(hubMetadataQuery.queryKey);
  const hubMetadata = useMatches()
    .flatMap((match) => (match.data as { appData?: DehydratedState } | undefined)?.appData?.queries ?? [])
    .find((query) => hashKey(query.queryKey) === hubMetadataKey)?.state.data as HubMetadata | undefined;
  return hubMetadata ? hqModeForPath(pathname, resolveHqColorMode(hubMetadata.background)) : undefined;
}

/** Writes the member's current HQ mode onto <html>, before paint, whenever it or the route changes. */
export function useSyncHqTheme() {
  const mode = useHqColorMode();
  useIsomorphicLayoutEffect(() => {
    const root = document.documentElement;
    if (mode) root.dataset.hqTheme = mode;
    else delete root.dataset.hqTheme;
  }, [mode]);
}
